/**
 * contact-commerce.tsx — Khối "Evotech" (hạng, điểm, tổng chi tiêu) và "Đơn hàng (n)"
 * ở hồ sơ khách: cột phải màn Hội thoại + trang chi tiết khách.
 * Dữ liệu từ tích hợp Evotech (docs/TICH-HOP-EVOTECH.md, P1).
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown, ChevronRight, Copy, CreditCard, ExternalLink, ImageOff, Link2, Loader2, MapPin, RefreshCw, ShoppingBag, StickyNote, Store, Ticket, Truck, Unlink,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { api, apiError } from '@/lib/api-client'
import { formatVnd } from '@/lib/format-money'
import { cn } from '@/lib/utils'
import { i18n } from '@/lib/i18n'
import { safeHttpUrl } from '@/lib/safe-url'
import { resolveImageUrl } from '@/hooks/use-products'
import {
  DELIVERY_LABEL,
  ORDER_ORIGIN_LABEL,
  ORDER_STATUS,
  PAY_STATUS,
  TRANSPORT_STATUS,
  TYPE_PAY_LABEL,
  statusLabel,
  useContactEvotech,
  useContactOrders,
  useLinkEvotech,
  useUnlinkEvotech,
  type EvotechMember,
  type EvotechMemberResult,
  type ExternalOrder,
  type ExternalOrderItem,
} from '@/hooks/use-evotech'

const SOURCE_LABEL: Record<string, string> = { evotech: 'Evotech' }

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d.getTime())
    ? '—'
    : `${d.toLocaleDateString(i18n.language || 'vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString(i18n.language || 'vi-VN', { hour: '2-digit', minute: '2-digit' })}`
}

const num = (v: number | null | undefined) => (v == null ? '—' : new Intl.NumberFormat('vi-VN').format(v))

/** Thẻ thành viên Evotech. Ẩn hẳn khi tổ chức chưa kết nối Evotech. */
export function EvotechMemberSection({ contactId }: { contactId: string }) {
  const { t } = useTranslation('convContact')
  const qc = useQueryClient()
  const { data, isLoading, isError, error } = useContactEvotech(contactId)
  const link = useLinkEvotech(contactId)
  const unlink = useUnlinkEvotech(contactId)
  const [refreshing, setRefreshing] = useState(false)
  const [manualNo, setManualNo] = useState('')

  if (isLoading) {
    return <section className="h-16 animate-pulse rounded-lg border bg-muted/30" />
  }
  if (!isError && data && !data.configured) return null

  const refresh = async () => {
    setRefreshing(true)
    try {
      const fresh = (await api.get<EvotechMemberResult>(`/contacts/${contactId}/evotech`, { params: { fresh: 1 } })).data
      qc.setQueryData(['evotech', 'member', contactId], fresh)
    } catch (e) {
      toast.error(apiError(e))
    } finally {
      setRefreshing(false)
    }
  }

  const doLink = (userNo: number) =>
    link.mutate(userNo, {
      onSuccess: () => {
        toast.success(t('commerce.linked'))
        setManualNo('')
      },
      onError: (e) => toast.error(apiError(e)),
    })

  return (
    <section className="space-y-2 rounded-lg border p-3">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-semibold">
          <img src="/apps/evotech.svg" alt="" className="h-3.5 w-3.5 rounded-sm" /> Evotech
        </p>
        <div className="flex items-center gap-0.5">
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title={t('commerce.refresh')} onClick={refresh} disabled={refreshing}>
            <RefreshCw className={cn('h-3 w-3', refreshing && 'animate-spin')} />
          </Button>
          {data?.configured && data.linked && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              title={t('commerce.unlink')}
              disabled={unlink.isPending}
              onClick={() => {
                if (!confirm(t('commerce.confirmUnlink'))) return
                unlink.mutate(undefined, { onError: (e) => toast.error(apiError(e)) })
              }}
            >
              <Unlink className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {isError ? (
        <p className="text-[11px] text-destructive">{apiError(error)}</p>
      ) : data?.configured && data.linked ? (
        <MemberCard m={data.member} />
      ) : data?.configured ? (
        <div className="space-y-2">
          <p className="text-[11px] text-muted-foreground">
            {data.reason === 'no_phone'
              ? t('commerce.noPhone')
              : data.reason === 'ambiguous'
                ? t('commerce.ambiguous')
                : t('commerce.notFound')}
          </p>
          {data.reason === 'ambiguous' &&
            (data.candidates ?? []).map((c) => (
              <div key={c.userNo} className="flex items-center gap-2 rounded-md bg-muted/50 px-2.5 py-1.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium">{c.fullname || t('commerce.customerN', { no: c.userNo })}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {t('commerce.codeLine', { no: c.userNo })}
                    {c.memberCode ? t('commerce.memberTag', { code: c.memberCode }) : ''}
                    {c.totalSpent != null ? t('commerce.spent', { amount: formatVnd(c.totalSpent) }) : ''}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={link.isPending} onClick={() => doLink(c.userNo)}>
                  {t('commerce.link')}
                </Button>
              </div>
            ))}
          <form
            className="flex items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault()
              const n = Number(manualNo)
              if (!Number.isInteger(n) || n <= 0) return toast.error(t('commerce.needUserNo'))
              doLink(n)
            }}
          >
            <Input
              value={manualNo}
              onChange={(e) => setManualNo(e.target.value.replace(/\D/g, ''))}
              placeholder={t('commerce.userNoPlaceholder')}
              inputMode="numeric"
              className="h-7 text-xs"
            />
            <Button type="submit" size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]" disabled={link.isPending || !manualNo}>
              {link.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />} {t('commerce.link')}
            </Button>
          </form>
        </div>
      ) : null}
    </section>
  )
}

function MemberCard({ m }: { m: EvotechMember }) {
  const { t } = useTranslation('convContact')
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-xs font-medium">{m.fullname || t('commerce.customerN', { no: m.userNo })}</p>
        {m.level && <Badge className="shrink-0 text-[10px]">{m.level}</Badge>}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <Stat label={t('commerce.points')} value={num(m.point)} />
        <Stat label={t('commerce.totalSpent')} value={m.totalSpent != null ? formatVnd(m.totalSpent) : '—'} />
        <Stat label={t('commerce.memberCode')} value={m.memberCode || '—'} />
        <Stat label={t('commerce.vouchersLeft')} value={num(m.vouchersCount)} />
      </div>
      {m.nextLevel && m.remainingSpend != null && (
        <p className="text-[10px] text-muted-foreground">
          {t('commerce.toNextLevel', { amount: formatVnd(m.remainingSpend), level: m.nextLevel })}
        </p>
      )}
      {m.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {m.tags.map((tg) => (
            <span key={tg} className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {tg}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-muted/50 px-2 py-1">
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <p className="truncate text-xs font-medium tabular-nums" title={value}>{value}</p>
    </div>
  )
}

/** Danh sách đơn hàng từ hệ thống ngoài (Evotech…), mới nhất trước. */
export function OrdersSection({ contactId, bare = false, source }: { contactId: string; bare?: boolean; source?: string }) {
  const { t } = useTranslation('convContact')
  const { data, isLoading, isError } = useContactOrders(contactId, source)
  const total = data?.total ?? 0
  const orders = data?.orders ?? []

  return (
    <section className={cn('space-y-2', !bare && 'rounded-lg border p-3')}>
      {!bare && (
        <p className="flex items-center gap-1.5 text-xs font-semibold">
          <ShoppingBag className="h-3.5 w-3.5 text-primary" /> {t('commerce.orders')}
          <span className="font-normal text-muted-foreground">({total})</span>
        </p>
      )}
      {isLoading ? (
        <div className="h-10 animate-pulse rounded-md bg-muted/40" />
      ) : isError ? (
        <p className="text-[11px] text-destructive">{t('commerce.ordersLoadFailed')}</p>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center gap-1 py-3 text-center">
          <ShoppingBag className="h-5 w-5 text-muted-foreground/60" />
          <p className="text-[11px] text-muted-foreground">{t('commerce.noOrders')}</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {orders.map((o) => (
            <OrderRow key={o.id} o={o} />
          ))}
          {total > orders.length && (
            <p className="text-center text-[10px] text-muted-foreground">{t('commerce.showing', { shown: orders.length, total })}</p>
          )}
        </div>
      )}
    </section>
  )
}

function StatusBadge({ map, v }: { map: typeof ORDER_STATUS; v: string | null }) {
  const s = statusLabel(map, v)
  if (!s) return null
  return (
    <Badge variant={s[1]} className="px-1.5 py-0 text-[10px]">
      {s[0]}
    </Badge>
  )
}

function OrderRow({ o }: { o: ExternalOrder }) {
  const { t } = useTranslation('convContact')
  const [open, setOpen] = useState(false)
  const items = Array.isArray(o.items) ? o.items : []
  const count = items.reduce((n, it) => n + (it.quantity || 0), 0)
  const thumbs = items.filter((it) => it.image).slice(0, 4)
  return (
    <div className="overflow-hidden rounded-md border bg-card">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full px-2.5 py-2 text-left hover:bg-muted/40" aria-expanded={open}>
        <div className="flex items-center gap-1.5">
          {open ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
          <span className="min-w-0 flex-1 truncate font-mono text-xs font-medium" title={o.externalId}>
            #{o.externalId}
          </span>
          <span className="shrink-0 text-xs font-semibold tabular-nums">{formatVnd(o.total)}</span>
        </div>
        <p className="mt-0.5 pl-[18px] text-[10px] text-muted-foreground">
          {fmtDate(o.placedAt)} · {SOURCE_LABEL[o.source] ?? o.source} · {t('commerce.productCount', { count: count || items.length })}
        </p>
        {!open && items.length > 0 && (
          <div className="mt-1.5 flex items-center gap-1.5 pl-[18px]">
            {thumbs.length > 0 ? (
              <div className="flex -space-x-1.5">
                {thumbs.map((it, i) => (
                  <ItemThumb key={`${it.itemNo ?? 'x'}-${i}`} src={it.image} className="h-7 w-7 ring-2 ring-card" />
                ))}
              </div>
            ) : null}
            <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
              {items.map((it) => it.productName || it.name).join(', ')}
            </span>
          </div>
        )}
        <div className="mt-1.5 flex flex-wrap gap-1 pl-[18px]">
          <StatusBadge map={ORDER_STATUS} v={o.status} />
          <StatusBadge map={PAY_STATUS} v={o.payStatus} />
          <StatusBadge map={TRANSPORT_STATUS} v={o.transportStatus} />
        </div>
      </button>
      {open && (
        <div className="border-t">
          {items.length > 0 && (
            <ul className="divide-y">
              {items.map((it, i) => (
                <OrderItemRow key={`${it.itemNo ?? 'x'}-${i}`} it={it} />
              ))}
            </ul>
          )}
          <OrderSummary o={o} />
        </div>
      )}
    </div>
  )
}

function ItemThumb({ src, className }: { src?: string | null; className?: string }) {
  const [broken, setBroken] = useState(false)
  const url = resolveImageUrl(src)
  return (
    <span className={cn('flex shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/50', className)}>
      {url && !broken ? (
        <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" onError={() => setBroken(true)} />
      ) : (
        <ImageOff className="h-3.5 w-3.5 text-muted-foreground/50" />
      )}
    </span>
  )
}

/** Mã hiển thị: SKU, không có thì mã sản phẩm Evotech (itemsCode). */
function itemCode(it: ExternalOrderItem): string | null {
  return it.sku?.trim() || it.itemsCode?.trim() || null
}

function OrderItemRow({ it }: { it: ExternalOrderItem }) {
  const { t } = useTranslation('convContact')
  const title = it.productName || it.name
  const code = itemCode(it)
  const copyCode = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!code) return
    navigator.clipboard?.writeText(code).then(
      () => toast.success(t('commerce.copiedCode', { code })),
      () => toast.error(t('commerce.copyFailed')),
    )
  }
  return (
    <li className="flex gap-2.5 px-2.5 py-2">
      <ItemThumb src={it.image} className="h-11 w-11" />
      <div className="min-w-0 flex-1 space-y-0.5">
        {safeHttpUrl(it.link) ? (
          <a
            href={safeHttpUrl(it.link)}
            target="_blank"
            rel="noopener noreferrer"
            className="line-clamp-2 text-xs font-medium leading-snug hover:text-primary hover:underline"
            title={t('commerce.openProduct')}
          >
            {title}
          </a>
        ) : (
          <p className="line-clamp-2 text-xs font-medium leading-snug" title={title}>{title}</p>
        )}
        {it.variant && <p className="text-[10.5px] text-muted-foreground">{it.variant}</p>}
        <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10.5px] text-muted-foreground">
          {code && (
            <button
              type="button"
              onClick={copyCode}
              className="inline-flex min-w-0 max-w-full items-center gap-0.5 rounded bg-muted px-1 font-mono hover:text-foreground"
              title={t('commerce.copyCode', { code })}
            >
              <span className="truncate">{code}</span>
              <Copy className="h-2.5 w-2.5 shrink-0" />
            </button>
          )}
          {it.isGift && <span className="rounded bg-success/10 px-1 text-success">{t('commerce.gift')}</span>}
          {safeHttpUrl(it.link) && (
            <a href={safeHttpUrl(it.link)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-primary hover:underline">
              <ExternalLink className="h-2.5 w-2.5" /> {t('commerce.viewProduct')}
            </a>
          )}
        </div>
        <p className="flex flex-wrap gap-x-1 text-[10.5px] tabular-nums text-muted-foreground">
          <span className="whitespace-nowrap">{it.quantity} × {it.price != null ? formatVnd(it.price) : '—'}</span>
          {it.listPrice != null && <span className="whitespace-nowrap line-through opacity-70">{formatVnd(it.listPrice)}</span>}
        </p>
      </div>
      <span className="shrink-0 text-xs font-medium tabular-nums">{it.total != null ? formatVnd(it.total) : '—'}</span>
    </li>
  )
}

function SummaryLine({ label, value, strong, tone }: { label: string; value: string; strong?: boolean; tone?: 'discount' }) {
  return (
    <div className={cn('flex items-baseline justify-between gap-2 text-[11px]', strong && 'text-xs font-semibold')}>
      <span className={cn(!strong && 'text-muted-foreground')}>{label}</span>
      <span className={cn('tabular-nums', tone === 'discount' && 'text-success')}>{value}</span>
    </div>
  )
}

function InfoLine({ icon: Icon, children }: { icon: typeof Truck; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-1.5 text-[11px]">
      <Icon className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1 break-words">{children}</div>
    </div>
  )
}

function OrderSummary({ o }: { o: ExternalOrder }) {
  const { t } = useTranslation('convContact')
  const d = o.details
  if (!d) return null
  const origin = d.origin ? ORDER_ORIGIN_LABEL[d.origin] ?? d.origin : null
  return (
    <div className="space-y-2 border-t bg-muted/30 px-2.5 py-2">
      <div className="space-y-0.5">
        {d.subtotal != null && <SummaryLine label={t('commerce.subtotal')} value={formatVnd(d.subtotal)} />}
        {d.discount != null && d.discount > 0 && <SummaryLine label={t('commerce.discount')} value={`−${formatVnd(d.discount)}`} tone="discount" />}
        {d.pointDiscount != null && d.pointDiscount > 0 && (
          <SummaryLine
            label={d.pointsUsed ? t('commerce.pointDeductN', { points: new Intl.NumberFormat('vi-VN').format(d.pointsUsed) }) : t('commerce.pointDeduct')}
            value={`−${formatVnd(d.pointDiscount)}`}
            tone="discount"
          />
        )}
        {d.shippingFee != null && d.shippingFee > 0 && <SummaryLine label={t('commerce.shippingFee')} value={formatVnd(d.shippingFee)} />}
        {d.tax != null && d.tax > 0 && <SummaryLine label={t('commerce.tax')} value={formatVnd(d.tax)} />}
        <SummaryLine label={t('commerce.total')} value={formatVnd(o.total)} strong />
      </div>
      <div className="space-y-1">
        {d.typePay && <InfoLine icon={CreditCard}>{TYPE_PAY_LABEL[d.typePay] ?? d.typePay}</InfoLine>}
        {d.delivery && (
          <InfoLine icon={d.delivery === 'InShop' ? Store : Truck}>
            {DELIVERY_LABEL[d.delivery] ?? d.delivery}
            {d.delivery === 'InShop' && d.branchName ? ` · ${d.branchName}` : ''}
            {d.shippingCarrier ? ` · ${d.shippingCarrier}` : ''}
          </InfoLine>
        )}
        {d.shipTo && d.delivery !== 'InShop' && (
          <InfoLine icon={MapPin}>
            <span className="font-medium">{[d.shipTo.name, d.shipTo.phone].filter(Boolean).join(' · ')}</span>
            {d.shipTo.address && <span className="block text-muted-foreground">{d.shipTo.address}</span>}
          </InfoLine>
        )}
        {origin && <InfoLine icon={ShoppingBag}>{t('commerce.channelLine', { origin })}</InfoLine>}
        {d.voucherCodes.length > 0 && (
          <InfoLine icon={Ticket}>
            <span className="flex flex-wrap gap-1">
              {d.voucherCodes.map((c) => (
                <span key={c} className="rounded border border-dashed px-1 font-mono text-[10px]">{c}</span>
              ))}
            </span>
          </InfoLine>
        )}
        {d.notes && <InfoLine icon={StickyNote}><span className="italic text-muted-foreground">{d.notes}</span></InfoLine>}
      </div>
    </div>
  )
}
